import {
  calculateExpressionSync,
  setExpressionFunctionCachedResultSync,
} from '@lmt/expressions';
import { VisitCardLineStyleDto, QueryFactory } from '@lmt/shared-types';
import { getQueriesFromVisitCardSettings } from '@lmt/unicorn-shared/app/components/calendar/helper';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import Logger from 'js-logger';
import React, { useEffect, useState } from 'react';
import { Field } from '@lmt/unicorn-shared/src/app/components/common/Field';
import { Input } from '@lmt/unicorn-shared/src/app/components/common/ui-kit/inputs/Input';
import styles from './PreviewPanel.module.scss';
import { VisitCardLine } from './types';
import VisitCard from './VisitCard';
import { useUniSelector } from '@lmt/unicorn-shared/app/store/types';
import { getSettings } from '@lmt/unicorn-shared/app/features/settings/selectors';
import { getBGColor } from '../../color-settings/components/color-parser';
import { isEmpty } from '../../../measurements/copy-datablock-field/common';
import { useEventCallback } from '@mui/material';
import { expressionErrorString } from './common';

interface Props {
  lineStyles: VisitCardLineStyleDto[];
  errors: string[];
  setErrors: (errors: string[]) => void;
  validationError: string;
}

const PreviewPanel: React.FC<Props> = React.memo(function PreviewPanel(props) {
  const { lineStyles, errors, setErrors, validationError } = props;
  const i18n = useScopedTranslation('calendar.visit_card_settings.preview');
  const isNightTheme = useUniSelector(getSettings).isNightTheme;
  const [value, setValue] = useState<string>('');
  const [visitId, setVisitId] = useState<number | null>(null);
  const [content, setContent] = useState<VisitCardLine[]>([]);

  // set default value of visitId
  const searchVisitQuery =
    QueryFactory.AppointmentEventsQuery.useSearchVisitsQuery({ limit: 1 });
  useEffect(() => {
    const data = searchVisitQuery.data;
    if (data && data.length > 0) {
      setVisitId(data[0].id);
      setValue(data[0].id.toString());
    }
  }, [searchVisitQuery.data]);

  const updateVisitPreview = useEventCallback(async () => {
    if (!visitId || isNaN(visitId)) {
      setContent([]);
      return;
    }
    try {
      const queries = await getQueriesFromVisitCardSettings(lineStyles);

      const visits =
        await QueryFactory.AppointmentEventsClient.searchAppointments(
          visitId,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          queries,
        );
      const visit = visits[0];

      if (visit && visit.calculatedQueries) {
        const expressionErrors: string[] = [];
        const lines: VisitCardLine[] = [];
        lineStyles.forEach((lineStyle) => {
          try {
            const key = setExpressionFunctionCachedResultSync(
              visit.calculatedQueries as any,
            );
            if (!lineStyle.expression) {
              return;
            }
            const value = calculateExpressionSync(lineStyle.expression, key);
            if (typeof value !== 'string' && typeof value !== 'number') {
              throw new Error(
                `${expressionErrorString}${lineStyle.expression}`,
              );
            }
            const style = {
              fontSize: `${lineStyle.fontSize}px`,
              paddingTop: `${lineStyle.paddingTop}px`,
              fontWeight: lineStyle.isBold ? 'bold' : '400',
            };
            const resultLine: VisitCardLine = {
              value: value as string,
              style,
            };
            lines.push(resultLine);
          } catch (err) {
            expressionErrors.push(convertToErrorString(err));
          }
        });
        if (isEmpty(expressionErrors)) {
          setContent(lines);
          setErrors([]);
        } else {
          setContent([]);
          setErrors(expressionErrors);
        }
      }
    } catch (error) {
      setErrors([convertToErrorString(error)]);
    }
  });

  useEffect(() => {
    updateVisitPreview().catch((e) => Logger.error(e));
  }, [visitId, lineStyles]);

  return (
    <div className={styles.container} data-test-id="preview-panel">
      <div className={styles.header}>
        <div className={styles.title}>{i18n.t('title')}</div>
      </div>
      <div>
        <Field title={i18n.t('visit_id')} className={styles.field}>
          <Input
            value={value}
            onChange={(e) => {
              setVisitId(parseInt(e.currentTarget.value));
              setValue(e.currentTarget.value);
            }}
            testId="preview-panel-input"
          />
        </Field>
      </div>
      {!isEmpty(errors) || validationError ? (
        <div className={styles.empty}>
          {errors
            .filter((x) => !!x)
            .map((x) => {
              return <div className={styles.errorText}>{x}</div>;
            })}
          {validationError && (
            <div className={styles.errorText}>{validationError}</div>
          )}
        </div>
      ) : content.length > 0 ? (
        <>
          <div className={styles.blockContainer}>
            <div className={styles.blockTitle}>
              {i18n.t('default_card_view')}
            </div>
            <div
              className={styles.default}
              data-test-id="preview-panel-default-view"
            >
              <VisitCard
                content={content}
                color={getBGColor('#E2DBFF', isNightTheme)}
              />
            </div>
          </div>
          <div className={styles.blockContainer}>
            <div className={styles.blockTitle}>
              {i18n.t('shortened_height_card_view')}
            </div>
            <div
              className={styles.shortenedHeight}
              data-test-id="preview-panel-shortened-height"
            >
              <VisitCard
                content={content}
                color={getBGColor('#D3F3E1', isNightTheme)}
              />
            </div>
            <div
              className={styles.shortenedHeight}
              style={{ paddingTop: '4px' }}
              data-test-id="preview-panel-shortened-height"
            >
              <VisitCard
                content={content}
                color={getBGColor('#E9F4FF', isNightTheme)}
              />
            </div>
          </div>
          <div className={styles.blockContainer}>
            <div className={styles.blockTitle}>
              {i18n.t('shortened_width_card_view')}
            </div>
            <div className={styles.shortenedWidthContainer}>
              <div
                className={styles.shortenedWidth}
                data-test-id="preview-panel-shortened-width"
              >
                <VisitCard
                  content={content}
                  color={getBGColor('#FBDFDF', isNightTheme)}
                />
              </div>
              <div
                className={styles.shortenedWidth}
                data-test-id="preview-panel-shortened-width"
              >
                <VisitCard
                  content={content}
                  color={getBGColor('#F4FBB1', isNightTheme)}
                />
              </div>
              <div
                className={styles.shortenedWidth}
                data-test-id="preview-panel-shortened-width"
              >
                <VisitCard
                  content={content}
                  color={getBGColor('#E2DBFF', isNightTheme)}
                />
              </div>
              <div
                className={styles.shortenedWidth}
                data-test-id="preview-panel-shortened-width"
              >
                <VisitCard
                  content={content}
                  color={getBGColor('#D3F3E1', isNightTheme)}
                />
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className={styles.empty}>
          <div className={styles.emptyText}>{i18n.t('empty_list_text')}</div>
        </div>
      )}
    </div>
  );
});

export default PreviewPanel;
